import { useState, useEffect } from "react";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { 
  Search, 
  Landmark, 
  MapPin, 
  DollarSign, 
  Percent, 
  Building2, 
  FileText,
  CheckCircle,
  Star,
  Filter,
  X,
  Mail,
  Phone,
  Globe,
  Clock,
  TrendingUp
} from "lucide-react";
import { DialerActions } from "../components/dialer/DialerComponents";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LendersSearchPage({ user, subscription, onLogout }) {
  const { mainClass } = useSidebar();
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLoanType, setSelectedLoanType] = useState("all");
  const [selectedState, setSelectedState] = useState("all");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [selectedLender, setSelectedLender] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const tier = subscription?.tier || user?.subscription_tier || "starter";

  const loanTypes = [
    { value: "all", label: "All Loan Types" },
    { value: "hard_money", label: "Hard Money" },
    { value: "private_money", label: "Private Money" },
    { value: "bridge", label: "Bridge Loan" },
    { value: "fix_flip", label: "Fix & Flip" },
    { value: "rental", label: "Rental/DSCR" },
    { value: "construction", label: "Construction" },
    { value: "commercial", label: "Commercial" }
  ];

  const states = [
    { value: "all", label: "All States" },
    { value: "TX", label: "Texas" },
    { value: "FL", label: "Florida" },
    { value: "CA", label: "California" },
    { value: "AZ", label: "Arizona" },
    { value: "GA", label: "Georgia" },
    { value: "NC", label: "North Carolina" },
    { value: "OH", label: "Ohio" },
    { value: "PA", label: "Pennsylvania" },
    { value: "TN", label: "Tennessee" },
    { value: "CO", label: "Colorado" }
  ];

  useEffect(() => {
    fetchLenders();
  }, []);

  const fetchLenders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("q", searchQuery);
      if (selectedLoanType !== "all") params.append("loan_type", selectedLoanType);
      if (selectedState !== "all") params.append("state", selectedState);
      if (minAmount) params.append("min_amount", minAmount);
      if (maxAmount) params.append("max_amount", maxAmount);

      const response = await axios.get(
        `${BACKEND_URL}/api/lenders/search?${params.toString()}`,
        { withCredentials: true }
      );
      setLenders(response.data);
    } catch (error) {
      console.error("Failed to fetch lenders:", error);
      // Show empty state instead of error for new feature
      setLenders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLenders();
  };

  const handleViewProfile = (lender) => {
    setSelectedLender(lender);
    setShowProfileModal(true);
  };

  const formatCurrency = (value) => {
    if (!value) return "N/A";
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  const getLoanTypeLabel = (type) => {
    const found = loanTypes.find(lt => lt.value === type);
    return found ? found.label : type;
  };

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`${mainClass} p-4 md:p-8 transition-all duration-300`} data-testid="lenders-search-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-white uppercase">Lender Marketplace</h1>
          <p className="text-white/70 mt-2">Find lenders for your deals - hard money, private money, bridge loans & more</p>
        </div>

        {/* Search & Filters */}
        <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-6 mb-6">
          <form onSubmit={handleSearch}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="lg:col-span-2">
                <Label className="text-white/70 text-xs uppercase mb-2 block">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by lender name or specialty..."
                    className="bg-black border-white/[0.08] text-white pl-10"
                    data-testid="lender-search-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/70 text-xs uppercase mb-2 block">Loan Type</Label>
                <Select value={selectedLoanType} onValueChange={setSelectedLoanType}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {loanTypes.map(type => (
                      <SelectItem key={type.value} value={type.value} className="text-white hover:bg-white/[0.06]">
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-white/70 text-xs uppercase mb-2 block">State</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
                    {states.map(state => (
                      <SelectItem key={state.value} value={state.value} className="text-white hover:bg-white/[0.06]">
                        {state.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <Button 
                  type="submit" 
                  className="w-full bg-gold text-black hover:bg-gold-light rounded-none font-bold"
                  data-testid="search-lenders-btn"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>

            {/* Amount Range */}
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/[0.08]">
              <div>
                <Label className="text-white/70 text-xs uppercase mb-2 block">Min Loan Amount</Label>
                <Input
                  type="number"
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  placeholder="$50,000"
                  className="bg-black border-white/[0.08] text-white"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase mb-2 block">Max Loan Amount</Label>
                <Input
                  type="number"
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  placeholder="$5,000,000"
                  className="bg-black border-white/[0.08] text-white"
                />
              </div>
            </div>
          </form>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : lenders.length === 0 ? (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] p-12 text-center">
            <Landmark className="w-16 h-16 text-white/70 mx-auto mb-4" />
            <h3 className="text-xl font-heading font-bold text-white mb-2">No Lenders Found</h3>
            <p className="text-white/70 mb-4">
              {tier === "starter" 
                ? "Upgrade to Pro to access the lender marketplace"
                : "Try adjusting your search filters or check back later"}
            </p>
            {tier !== "starter" && (
              <Button 
                onClick={() => {
                  setSearchQuery("");
                  setSelectedLoanType("all");
                  setSelectedState("all");
                  setMinAmount("");
                  setMaxAmount("");
                  fetchLenders();
                }}
                variant="outline"
                className="border-white/[0.1] text-white/70 rounded-none"
              >
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="text-sm text-white/70 mb-4">
              Found {lenders.length} lender{lenders.length !== 1 ? 's' : ''}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lenders.map((lender) => (
                <div 
                  key={lender.lender_id} 
                  className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden hover:border-white/[0.15] transition-all duration-300 group"
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading font-bold text-white text-lg">{lender.company_name}</h3>
                        {lender.is_verified && (
                          <span className="inline-flex items-center gap-1 text-xs text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] mt-1">
                            <CheckCircle className="w-3 h-3" />
                            Verified Lender
                          </span>
                        )}
                      </div>
                      <div className="p-2 bg-black border border-white/[0.08]">
                        <Landmark className="w-6 h-6 text-gold" />
                      </div>
                    </div>

                    {/* Loan Types */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {lender.loan_types?.slice(0, 3).map((type, idx) => (
                          <span 
                            key={idx} 
                            className="text-xs px-2 py-1 bg-black text-white/70 border border-white/[0.08]"
                          >
                            {getLoanTypeLabel(type)}
                          </span>
                        ))}
                        {lender.loan_types?.length > 3 && (
                          <span className="text-xs px-2 py-1 bg-black text-white/70">
                            +{lender.loan_types.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Key Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-white/70 uppercase">Loan Range</p>
                        <p className="text-sm text-white font-mono">
                          {formatCurrency(lender.min_loan_amount)} - {formatCurrency(lender.max_loan_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-white/70 uppercase">Max LTV</p>
                        <p className="text-sm text-white font-mono">
                          {lender.max_ltv || 70}%
                        </p>
                      </div>
                    </div>

                    {/* States */}
                    {lender.states_served && lender.states_served.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-white/70 mb-4">
                        <MapPin className="w-4 h-4 text-white/70" />
                        <span className="truncate">
                          {lender.states_served.slice(0, 4).join(", ")}
                          {lender.states_served.length > 4 && ` +${lender.states_served.length - 4}`}
                        </span>
                      </div>
                    )}

                    {/* Action */}
                    <Button 
                      onClick={() => handleViewProfile(lender)}
                      className="w-full bg-black border border-white/[0.08] text-white hover:bg-white/[0.06] hover:border-gold rounded-none"
                      data-testid={`view-lender-${lender.lender_id}`}
                    >
                      View Profile
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Lender Profile Modal */}
        <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
          <DialogContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase flex items-center gap-3">
                <Landmark className="w-6 h-6 text-gold" />
                {selectedLender?.company_name}
              </DialogTitle>
            </DialogHeader>

            {selectedLender && (
              <div className="space-y-6 mt-4">
                {/* Verification Badge */}
                {selectedLender.is_verified && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    <span className="text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] font-medium">Verified Lender</span>
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedLender.contact_email && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Mail className="w-4 h-4 text-white/70" />
                      <span className="text-sm">{selectedLender.contact_email}</span>
                    </div>
                  )}
                  {selectedLender.contact_phone && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Phone className="w-4 h-4 text-white/70" />
                      <span className="text-sm">{selectedLender.contact_phone}</span>
                    </div>
                  )}
                  {selectedLender.website && (
                    <div className="flex items-center gap-2 text-white/70">
                      <Globe className="w-4 h-4 text-white/70" />
                      <a href={selectedLender.website} target="_blank" rel="noopener noreferrer" className="text-sm text-gold hover:text-gold-light">
                        {selectedLender.website}
                      </a>
                    </div>
                  )}
                </div>
                
                {/* Quick Contact via Dialer */}
                {selectedLender.contact_phone && (
                  <div className="bg-black border border-white/[0.08] p-4">
                    <h4 className="font-heading font-bold text-white uppercase mb-3">Quick Contact</h4>
                    <DialerActions
                      phoneNumber={selectedLender.contact_phone}
                      contactId={selectedLender.lender_id}
                      contactType="lender"
                      contactName={selectedLender.company_name}
                      size="default"
                      showLabels={true}
                    />
                  </div>
                )}

                {/* Lending Criteria */}
                <div className="bg-black border border-white/[0.08] p-4">
                  <h4 className="font-heading font-bold text-white uppercase mb-4">Lending Criteria</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Loan Amount Range</p>
                      <p className="text-white font-mono">
                        {formatCurrency(selectedLender.min_loan_amount)} - {formatCurrency(selectedLender.max_loan_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Max LTV</p>
                      <p className="text-white font-mono">{selectedLender.max_ltv || 70}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Max ARV</p>
                      <p className="text-white font-mono">{selectedLender.max_arv || 70}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Typical Rate</p>
                      <p className="text-white font-mono">
                        {selectedLender.typical_rate ? `${selectedLender.typical_rate}%` : "Contact for rates"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Typical Points</p>
                      <p className="text-white font-mono">
                        {selectedLender.typical_points ? `${selectedLender.typical_points} pts` : "Contact for terms"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/70 uppercase mb-1">Closing Time</p>
                      <p className="text-white font-mono">
                        {selectedLender.typical_closing_days ? `${selectedLender.typical_closing_days} days` : "Varies"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Loan Types */}
                <div>
                  <h4 className="font-heading font-bold text-white uppercase mb-3">Loan Types Offered</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedLender.loan_types?.map((type, idx) => (
                      <span 
                        key={idx} 
                        className="text-sm px-3 py-1.5 bg-gold/10 text-gold border border-gold/30"
                      >
                        {getLoanTypeLabel(type)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Property Types */}
                {selectedLender.property_types && selectedLender.property_types.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-3">Property Types</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedLender.property_types.map((type, idx) => (
                        <span 
                          key={idx} 
                          className="text-sm px-3 py-1.5 bg-black text-white/70 border border-white/[0.08]"
                        >
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* States Served */}
                {selectedLender.states_served && selectedLender.states_served.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-3">States Served</h4>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-white/70" />
                      <span className="text-white/70">
                        {selectedLender.states_served.join(", ")}
                      </span>
                    </div>
                  </div>
                )}

                {/* Description */}
                {selectedLender.description && (
                  <div>
                    <h4 className="font-heading font-bold text-white uppercase mb-3">About</h4>
                    <p className="text-white/70 text-sm">{selectedLender.description}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-white/[0.08]">
                  <Button 
                    onClick={() => setShowProfileModal(false)}
                    variant="outline"
                    className="flex-1 border-white/[0.1] text-white/70 rounded-none"
                  >
                    Close
                  </Button>
                  <Button 
                    onClick={() => {
                      toast.success("Contact info copied! You can now reach out to this lender.");
                      setShowProfileModal(false);
                    }}
                    className="flex-1 gold-gradient-btn rounded-none font-bold"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Contact Lender
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
